import { queryClient } from "@/client";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { LoadEmailSelectOptionsRequestDto } from "@advanced-sales-technologies/backend-client/models/LoadEmailSelectOptionsRequestDto";
import { InfiniteData, QueryKey, useMutation } from "@tanstack/react-query";

import { PaginatedData } from "@/types/InfiniteQuery.type";
import useSalesyApiClient from "@/hooks/useSalesyApiClient";

type OptionParams = {
  id: string;
  companyId: string;
  listId?: string | null;
  option: LoadEmailSelectOptionsRequestDto;
  queryKey?: QueryKey;
};

export const personEmailMutationKey = (companyId: string) => ["get-external-person-emails", companyId];

export const useGetPersonEmails = (companyId: string) => {
  const { apiClient } = useSalesyApiClient();

  const mutation = useMutation({
    mutationKey: personEmailMutationKey(companyId),
    mutationFn: (optionParams: OptionParams) =>
      apiClient.selectOptions.loadContactPersonEmailOptions({
        id: optionParams.id,
        companyId: optionParams.companyId,
        requestBody: optionParams.option,
      }),
    onSuccess: (updatedData, variables) => {
      if (!variables.queryKey) return;
      queryClient.setQueryData<InfiniteData<PaginatedData<CompanyEntryDto>>>(variables.queryKey, (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.map((item) => (item.id === variables.companyId ? updatedData : item)),
          })),
        };
      });
    },
  });

  const { mutate, ...mutationResult } = mutation;

  return {
    ...mutationResult,
    getEmails: mutate,
  };
};
